import type { LucideIcon } from "lucide-react";
import {
  Bell, BellRing, Building2, ClipboardCheck, Database, DatabaseBackup, FileSearch, Fingerprint,
  Gauge, HeartPulse, Import, KeyRound, LayoutDashboard, MessageSquareText,
  MonitorSmartphone, PackageCheck, Settings as SettingsIcon, Smartphone, UserRound,
  Users,
} from "lucide-react";
import type { AdminShellMessageKey } from "./adminShellCatalog";
import type { DeviceForensicAdminMessageKey } from "./deviceForensicAdminCatalog";
import type { EvidenceStorageClass } from "./operationsRuntime";

export type PageId = "overview" | "people" | "organizations" | "cases" | "mobile-security" | "secure-helpdesk" | "deep-scan" | "devices" | "parser-releases" | "collector-releases" | "wellness" |
  "imports" | "risk" | "intelligence" | "content" | "policies" | "communications" |
  "audit" | "auth" | "settings" | "storage-manager" | "maintenance" | "notifications" | "profile" | "send-notifications";

export type NavigationItem = {
  id: PageId;
  labelKey: AdminShellMessageKey;
  icon: LucideIcon;
};
export type AdminSearchResult = {
  id: string;
  page: PageId;
  label: string;
  description: string;
  searchable: string;
  kind: string;
  icon: LucideIcon;
};

export const EVIDENCE_STORAGE_CLASSES: Array<{
  value: EvidenceStorageClass;
  labelKey: DeviceForensicAdminMessageKey;
}> = [
  { value: "custody-core", labelKey: "lifecycleClass.custody-core" },
  { value: "derived-investigation", labelKey: "lifecycleClass.derived-investigation" },
  { value: "deep-raw", labelKey: "lifecycleClass.deep-raw" },
  { value: "volatile-memory", labelKey: "lifecycleClass.volatile-memory" },
  { value: "volatile-network", labelKey: "lifecycleClass.volatile-network" },
  { value: "full-storage-image", labelKey: "lifecycleClass.full-storage-image" },
  { value: "analysis-scratch", labelKey: "lifecycleClass.analysis-scratch" },
];

export const STORAGE_SECTION_EVENT = "kavoosh:storage-manager-section" as const;
export const STORAGE_MANAGER_SECTIONS = [
  ["overview", "storageOverviewTitle"],
  ["locations", "storageLocationsTitle"],
  ["inventory", "inventoryTitle"],
  ["activity", "storageOperationsNav"],
  ["storage", "storageCapacityNav"],
  ["lifecycle", "storageRetentionNav"],
] as const;

export const NAVIGATION: Array<{
  labelKey: AdminShellMessageKey | null;
  items: NavigationItem[];
}> = [
  { labelKey: null, items: [
    { id: "overview", labelKey: "page.overview", icon: LayoutDashboard },
  ] },
  { labelKey: "group.directory", items: [
    { id: "people", labelKey: "page.people", icon: Users },
    { id: "organizations", labelKey: "page.organizations", icon: Building2 },
  ] },
  { labelKey: "group.operations", items: [
    { id: "cases", labelKey: "page.cases", icon: FileSearch },
    { id: "mobile-security", labelKey: "page.mobile-security", icon: Smartphone },
    { id: "devices", labelKey: "page.devices", icon: MonitorSmartphone },
    { id: "collector-releases", labelKey: "page.collector-releases", icon: PackageCheck },
    { id: "wellness", labelKey: "page.wellness", icon: HeartPulse },
    { id: "imports", labelKey: "page.imports", icon: Import },
  ] },
  { labelKey: "group.risk", items: [
    { id: "risk", labelKey: "page.risk", icon: Gauge },
    { id: "parser-releases", labelKey: "page.parser-releases", icon: PackageCheck },
    { id: "intelligence", labelKey: "page.intelligence", icon: Database },
  ] },
  { labelKey: "group.platform", items: [
    { id: "content", labelKey: "page.content", icon: MessageSquareText },
    { id: "send-notifications", labelKey: "page.send-notifications", icon: BellRing },
    { id: "policies", labelKey: "page.policies", icon: ClipboardCheck },
    { id: "audit", labelKey: "page.audit", icon: Fingerprint },
    { id: "auth", labelKey: "page.auth", icon: KeyRound },
    { id: "storage-manager", labelKey: "page.storage-manager", icon: Database },
    { id: "maintenance", labelKey: "page.maintenance", icon: DatabaseBackup },
    { id: "settings", labelKey: "page.settings", icon: SettingsIcon },
  ] },
];

export const ALL_ITEMS = NAVIGATION.flatMap((group) => group.items);
export const SEARCHABLE_ROUTES: NavigationItem[] = [
  ...ALL_ITEMS,
  { id: "communications", labelKey: "page.communications", icon: MessageSquareText },
  { id: "notifications", labelKey: "page.notifications", icon: Bell },
  { id: "profile", labelKey: "page.profile", icon: UserRound },
];

export function titleForPage(
  page: PageId,
  t: (key: AdminShellMessageKey, values?: Record<string, string | number>) => string,
): string {
  const key = SEARCHABLE_ROUTES.find((item) => item.id === page)?.labelKey ?? "page.overview";
  return t(key);
}

export function pageFromPath(path: string): PageId {
  const candidate = path.split("/").filter(Boolean)[1] as PageId | undefined;
  if (candidate === "secure-helpdesk" || candidate === "deep-scan") return "mobile-security";
  return SEARCHABLE_ROUTES.some((item) => item.id === candidate) ? candidate! : "overview";
}
